'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgWarningFill from '@common/icon/SvgWarningFill';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { CustomLink } from '@/app/_components/CustomLink';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { ShareButton } from '@/app/donation/_components/ShareButton';
import { calculateRequiredDonationAmount } from '@/app/donation/_utils/giftOrder';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useScrollToSection } from '@/hooks/useScrollToSection';
import { useGiftOption } from '../../../_context/GiftOptionContext';
import { DELIVERY_PERIOD_LABEL } from '../../../constants';
import { PriceInfo } from '../../GiftDetailHeader/_components/PriceInfo';
import { PrefillForm } from '../PrefillForm';
import { DesktopMainImages } from './DesktopMainImages';
import { MobileHero } from './MobileHero';
interface HeaderInfoSectionProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  reviewRating?: number;
  reviewCount: number;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  productData?: GiftProductDetail;
  className?: string;
}
export const HeaderInfoSection = t0 => {
  const $ = _c(75);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    reviewCount,
    reviewRating,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    productData,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const {
    scrollToSectionWithTab
  } = useScrollToSection();
  const {
    selectedOptions
  } = useGiftOption();
  let t1;
  if ($[0] !== selectedOptions) {
    t1 = Array.from(selectedOptions.values()).reduce(_temp, 0);
    $[0] = selectedOptions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const totalExtraProductPrice = t1;
  let t2;
  if ($[2] !== productData?.currentPrice || $[3] !== requiredDonationAmount || $[4] !== selectedOptions.size || $[5] !== totalExtraProductPrice) {
    t2 = selectedOptions.size > 0 ? calculateRequiredDonationAmount((productData?.currentPrice ?? 0) + totalExtraProductPrice) : requiredDonationAmount;
    $[2] = productData?.currentPrice;
    $[3] = requiredDonationAmount;
    $[4] = selectedOptions.size;
    $[5] = totalExtraProductPrice;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const selectedDonationAmount = t2;
  const isStoppedSale = productSaleStatus === GIFT_SALE_STATUS.STOPPED;
  const institutionRouteId = productData?.institutionSlugId ?? productData?.institutionId;
  let t3;
  if ($[7] !== onlineProductYN || $[8] !== productData?.deliveryExclusionRegionList) {
    t3 = onlineProductYN ? "" : (productData?.deliveryExclusionRegionList ?? []).map(_temp2).join(", ");
    $[7] = onlineProductYN;
    $[8] = productData?.deliveryExclusionRegionList;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const deliveryExclusionRegionNames = t3;
  let t4;
  if ($[10] !== isDesktopViewport || $[11] !== reviewCount || $[12] !== reviewRating) {
    t4 = isDesktopViewport ? <><Rating value={reviewRating ?? 0} size={24} />{reviewCount > 0 ? <span className="font-600 text-Gray-600 underline typo-body3">{reviewCount.toLocaleString()}건 후기 보기</span> : null}</> : <><Rating value={reviewRating ?? 0} starCount={1} size={isDesktopViewport ? 24 : 20} /><span className={cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4")}>{(reviewRating ?? 0).toFixed(1)}/5({reviewCount.toLocaleString()})</span></>;
    $[10] = isDesktopViewport;
    $[11] = reviewCount;
    $[12] = reviewRating;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const ratingSummary = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("grid grid-cols-2 gap-28", "tablet:grid-cols-1 tablet:gap-0", "mobile:grid-cols-1 mobile:gap-0", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== adultYN || $[17] !== heroImageUrlList) {
    t6 = <MobileHero heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[16] = adultYN;
    $[17] = heroImageUrlList;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== adultYN || $[20] !== heroImageUrlList || $[21] !== title) {
    t7 = <DesktopMainImages title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} />;
    $[19] = adultYN;
    $[20] = heroImageUrlList;
    $[21] = title;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("mobile-tablet:relative mobile-tablet:z-[1] mobile-tablet:-mt-20", "mobile-tablet:rounded-t-large mobile-tablet:bg-Gray-white mobile-tablet:pt-24");
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("flex flex-col px-20", "mobile-tablet:px-24");
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== institutionName || $[26] !== institutionRouteId) {
    t10 = institutionRouteId ? <CustomLink href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: institutionRouteId
    })} aria-label={`${institutionName} 상세 페이지로 이동`} className={cn("flex w-fit items-center gap-2 rounded-xsmall font-600 text-Gray-600 typo-body3", "mobile-tablet:font-400 mobile-tablet:typo-body4", "hover:underline active:opacity-80", FOCUS_RING)}>{institutionName}<SvgChevronright className="size-16 shrink-0 mobile-tablet:hidden" /></CustomLink> : <p className={cn("font-400 text-Gray-600 typo-body3", "mobile-tablet:typo-body4")}>{institutionName}</p>;
    $[25] = institutionName;
    $[26] = institutionRouteId;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ShareButton />;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10) {
    t12 = <div className="mb-8 flex items-center justify-between gap-4">{t10}{t11}</div>;
    $[29] = t10;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = cn("mb-12 flex justify-between gap-16", "mobile-tablet:mb-16");
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = isStoppedSale ? "text-Gray-400" : "text-Gray-900 mobile-tablet:text-Gray-black";
  let t15;
  if ($[32] !== t14) {
    t15 = cn("flex-1 break-all font-700 typo-h3", "tablet:font-600 tablet:typo-body1", "mobile:font-600 mobile:typo-body1", t14);
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== productName || $[35] !== t15) {
    t16 = <div className={t13}><h1 className={t15}>{productName}</h1></div>;
    $[34] = productName;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = cn("mb-28 flex flex-wrap items-center gap-8", "mobile-tablet:mb-16 mobile-tablet:gap-4");
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== ratingSummary || $[39] !== reviewCount || $[40] !== scrollToSectionWithTab) {
    t18 = <div className={t17}>{reviewCount > 0 ? <button type="button" onClick={() => scrollToSectionWithTab("review")} className={cn("flex items-center gap-8 rounded-xsmall transition-opacity duration-200", "mobile-tablet:ml-auto mobile-tablet:gap-4", "hover:opacity-80", "active:opacity-80", "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500")}>{ratingSummary}</button> : <div className={cn("flex items-center gap-8", "mobile-tablet:ml-auto mobile-tablet:gap-4")}>{ratingSummary}</div>}</div>;
    $[38] = ratingSummary;
    $[39] = reviewCount;
    $[40] = scrollToSectionWithTab;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== selectedDonationAmount) {
    t19 = <div className="mb-20"><PriceInfo amount={selectedDonationAmount} /></div>;
    $[42] = selectedDonationAmount;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t12 || $[45] !== t16 || $[46] !== t18 || $[47] !== t19) {
    t20 = <div className={t9}>{t12}{t16}{t18}{t19}</div>;
    $[44] = t12;
    $[45] = t16;
    $[46] = t18;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = cn("mx-20 flex flex-col gap-24 border-t border-Gray-200 pb-16 pt-20", "mobile-tablet:mx-0 mobile-tablet:gap-16 mobile-tablet:p-20");
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== onlineProductDesc || $[51] !== onlineProductYN) {
    t22 = onlineProductYN ? <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">온라인 답례품</p></div>{onlineProductDesc ? <div className="flex items-start gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">답례품 설명</p><p className="whitespace-pre-line text-Gray-550 mobile-tablet:text-Gray-900">{onlineProductDesc}</p></div> : null}</div> : <div className={cn("flex flex-col gap-16 font-500 typo-body3", "mobile-tablet:gap-12 mobile-tablet:font-400 mobile-tablet:typo-body4")}><div className="flex items-center gap-56 mobile-tablet:gap-16"><p className="w-92 text-Gray-600">배송 정보</p><p className="text-Gray-550 mobile-tablet:text-Gray-900">무료배송</p></div><div className="flex gap-56 mobile-tablet:gap-16"><p className="w-92 shrink-0 text-Gray-600">배송 안내</p><div className={cn("flex flex-wrap gap-x-4 text-Gray-550 mobile-tablet:text-Gray-900", "mobile-tablet:flex-col mobile-tablet:gap-4")}><p>{DELIVERY_PERIOD_LABEL}</p><p className="mobile-tablet:font-500">(식품의 경우 지연 발생 시 1주~2주 소요)</p></div></div></div>;
    $[50] = onlineProductDesc;
    $[51] = onlineProductYN;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== deliveryExclusionRegionNames || $[54] !== exchangeYN) {
    t23 = !exchangeYN || deliveryExclusionRegionNames ? <ul className="flex flex-col gap-8 rounded-xsmall border border-Gray-300 px-20 py-12 font-400 text-Gray-600 typo-caption1">{!exchangeYN ? <li className="flex items-center gap-4"><SvgWarningFill className="size-13 shrink-0 text-B-500" /><p>해당 답례품은 반품/교환이 불가해요.</p></li> : null}{deliveryExclusionRegionNames ? <li className="flex items-start gap-4"><SvgWarningFill className="mt-3 size-13 shrink-0 text-B-500" /><p className="shrink-0">배송불가 지역</p><span aria-hidden={true} className="text-Gray-500 typo-caption2">|</span><p className="text-Gray-900">{deliveryExclusionRegionNames}</p></li> : null}</ul> : null;
    $[53] = deliveryExclusionRegionNames;
    $[54] = exchangeYN;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t22 || $[57] !== t23) {
    t24 = <div className={t21}>{t22}{t23}</div>;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== productData || $[60] !== productNumber) {
    t25 = <PrefillForm className="px-20" productData={productData} productNumber={productNumber} />;
    $[59] = productData;
    $[60] = productNumber;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Skeleton className="h-56 w-full rounded-medium" />;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== productNumber) {
    t27 = <div className="hidden px-20 pt-24 mobile-tablet:block"><Suspense fallback={t26}><EventRewardCard productNumber={productNumber} defaultExpanded={true} /></Suspense></div>;
    $[63] = productNumber;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t20 || $[66] !== t24 || $[67] !== t25 || $[68] !== t27) {
    t28 = <div className={t8}><section className="flex w-full flex-col">{t20}{t24}{t25}{t27}</section></div>;
    $[65] = t20;
    $[66] = t24;
    $[67] = t25;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t28 || $[71] !== t5 || $[72] !== t6 || $[73] !== t7) {
    t29 = <div className={t5}>{t6}{t7}{t28}</div>;
    $[70] = t28;
    $[71] = t5;
    $[72] = t6;
    $[73] = t7;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  return t29;
};
function _temp(total, option) {
  return total + option.extraProductPrice;
}
function _temp2(region) {
  return region.regionName;
}